/**
 * Onboarding hub jump URLs (Wave 3 AC-10) — full account-settings / kiosks paths.
 * Forbidden: bare `/{tenant}?section=…`.
 * `test` is recommended / status-only (Wave 4) — never a Continue or row-jump target.
 * Legacy API id `needs` stays jumpable only as a remap to kiosks (wizard UI removed; no needs tab).
 */
import { buildDashboardSectionPathname } from '../../dashboard/utils/dashboardUrlBootstrap.js';

export type OnboardingHubJumpItemId =
  | 'identity'
  | 'needs'
  | 'salesPoints'
  | 'bank'
  | 'goLive';

export type OnboardingHubProgressItemId = OnboardingHubJumpItemId | 'test';

const ONBOARDING_HUB_JUMPABLE_IDS: ReadonlySet<string> = new Set<OnboardingHubJumpItemId>([
  'identity',
  'needs',
  'salesPoints',
  'bank',
  'goLive',
]);

/**
 * True when the checklist id has a navigate target (excludes `test` / unknown).
 * Does not require a tenant — use with {@link buildOnboardingHubJumpUrl} for the URL.
 */
export function isOnboardingHubJumpableItemId(itemId: string): itemId is OnboardingHubJumpItemId {
  return ONBOARDING_HUB_JUMPABLE_IDS.has(itemId);
}

/**
 * First incomplete id that Continue / jump may target — skips `test` and unknowns.
 */
export function pickFirstJumpableOnboardingHubItemId(
  incompleteItemIds: readonly string[],
): string | null {
  for (const itemId of incompleteItemIds) {
    if (isOnboardingHubJumpableItemId(itemId)) {
      return itemId;
    }
  }
  return null;
}

/**
 * Returns navigate URL for a hub checklist item, or null for non-jump (`test`) / unknown.
 * API remaps retired `needs` → kiosks (same as salesPoints) so old bookmarks do not 404.
 */
export function buildOnboardingHubJumpUrl(
  tenantCode: string,
  itemId: string,
): string | null {
  if (tenantCode.trim().length === 0) {
    return null;
  }
  if (!isOnboardingHubJumpableItemId(itemId)) {
    return null;
  }

  // Keep `needs` remap — API may still emit the id; no wizard UI.
  if (itemId === 'salesPoints' || itemId === 'needs') {
    const pathname = buildDashboardSectionPathname(tenantCode, 'kiosks');
    if (pathname === null) {
      return null;
    }
    return `${pathname}?section=kiosks`;
  }

  const accountPath = buildDashboardSectionPathname(tenantCode, 'account-settings');
  if (accountPath === null) {
    return null;
  }

  switch (itemId) {
    case 'identity':
      return `${accountPath}?section=account-settings&tab=org#acct-section-org-identity`;
    case 'bank': {
      const bankPath = buildDashboardSectionPathname(tenantCode, 'bank-accounts');
      if (bankPath === null) {
        return null;
      }
      return `${bankPath}?section=bank-accounts#acct-section-bank`;
    }
    case 'goLive':
      return `${accountPath}?section=account-settings&tab=goLive#acct-section-go-live`;
    default: {
      const _exhaustive: never = itemId;
      return _exhaustive;
    }
  }
}
